"use client";

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from "lucide-react";
import { useToast, Toast, ToastType } from "../../contexts/ToastContext";

// Map toast types to their corresponding styling and icons
const toastTypeConfig: Record<
    ToastType,
    { bgColor: string; textColor: string; icon: React.ReactNode }
> = {
    success: {
        bgColor: "bg-green-100 dark:bg-green-800/20",
        textColor: "text-green-800 dark:text-green-300",
        icon: <CheckCircle className="w-5 h-5 text-green-500" />,
    },
    error: {
        bgColor: "bg-red-100 dark:bg-red-800/20",
        textColor: "text-red-800 dark:text-red-300",
        icon: <AlertCircle className="w-5 h-5 text-red-500" />,
    },
    warning: {
        bgColor: "bg-yellow-100 dark:bg-yellow-800/20",
        textColor: "text-yellow-800 dark:text-yellow-300",
        icon: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
    },
    info: {
        bgColor: "bg-blue-100 dark:bg-blue-800/20",
        textColor: "text-blue-800 dark:text-blue-300",
        icon: <Info className="w-5 h-5 text-blue-500" />,
    },
};

const ToastItem: React.FC<{ toast: Toast; onClose: () => void }> = ({
                                                                        toast,
                                                                        onClose,
                                                                    }) => {
    const { bgColor, textColor, icon } = toastTypeConfig[toast.type];

    return (
        <motion.div
            key={toast.id}
            layout
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
                duration: 0.4
            }}
            className={`flex items-center p-4 mb-3 rounded-lg shadow-lg ${bgColor} max-w-md w-full`}
        >
            <div className="flex-shrink-0 mr-3">{icon}</div>
            <div className={`flex-1 ${textColor}`}>{toast.message}</div>
            <button
                onClick={onClose}
                className="flex-shrink-0 ml-3 p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
            >
                <X className="w-4 h-4 text-emerald-600 dark:text-emerald-500" />
            </button>
        </motion.div>
    );
};

export const ToastContainer: React.FC = () => {
    const { toasts, hideToast } = useToast();

    return (
        <div className="fixed top-4 right-4 z-50 flex flex-col items-end">
            <AnimatePresence>
                {toasts.map((toast) => (
                    <ToastItem
                        key={toast.id}
                        toast={toast}
                        onClose={() => hideToast(toast.id)}
                    />
                ))}
            </AnimatePresence>
        </div>
    );
};
